import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Check, X, AlertCircle } from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFDocumentSigner = ({ fileUrl, signatureFields = [], userRole, onComplete, onCancel }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth] = useState(500);
  const [activeField, setActiveField] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [externalMeta, setExternalMeta] = useState({}); // { [field_id]: { name, company, role } }
  const [externalPrompt, setExternalPrompt] = useState(null); // { field_id } to prompt kiosk details
  const [isDrawing, setIsDrawing] = useState(false);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // Helpers to classify field types
  const INFO_FIELD_TYPES = ['job_title', 'ticket_number', 'date_field', 'notes', 'printed_name', 'company_name', 'text_supervisor_fill', 'date_supervisor_fill', 'external_name', 'external_company'];
  const EXTERNAL_FIELD_TYPES = ['signature_external', 'external_name', 'external_company'];
  const SUPERVISOR_FILL_TYPES = ['text_supervisor_fill', 'date_supervisor_fill'];
  const isInfoField = (f) => INFO_FIELD_TYPES.includes(f.field_type);
  const isExternalField = (f) => EXTERNAL_FIELD_TYPES.includes(f.field_type) || f.signer_type === 'external';
  const isSupervisorFillField = (f) => SUPERVISOR_FILL_TYPES.includes(f.field_type) || f.signer_type === 'supervisor_fill';
  const isSignatureField = (f) => !f.field_type || f.field_type.startsWith('signature') || f.is_both || ['supervisor', 'employee', 'both', 'all_employees', 'specific_role', 'specific_employee'].includes(f.signer_type);

  // Determine which fields this user needs to fill
  const isSupervisor = ['admin', 'manager', 'supervisor'].includes(userRole);
  const myFields = signatureFields.filter(f => {
    // External fields only shown to supervisors (kiosk mode — supervisor hands tablet to external person)
    if (isExternalField(f)) return isSupervisor;
    // Supervisor-fill fields only shown to supervisors
    if (isSupervisorFillField(f)) return isSupervisor;
    // Info fields (text/date/notes) are always visible for the current signer
    if (isInfoField(f)) return true;
    if (f.is_both || f.field_type === 'signature_both') return true;
    if ((f.signer_type === 'supervisor' || f.field_type === 'signature_supervisor') && isSupervisor) return true;
    if ((f.signer_type === 'employee' || f.field_type === 'signature_employee') && !isSupervisor) return true;
    if (f.signer_type === 'all_employees') return true;
    if (f.signer_type === 'specific_role') return true;
    if (f.signer_type === 'specific_employee') return true;
    // Default: employee fields for workers
    if (f.signer_type === 'employee') return true;
    return false;
  });

  // If no specific fields are defined, show a single generic sign
  const hasPlacedFields = signatureFields.length > 0;

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth - 16;
        setPageWidth(Math.min(w, 700));
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    // Jump to the first page with a field that needs signing
    if (myFields.length > 0) {
      setCurrentPage(myFields[0].page || 1);
    }
  }, [myFields]);

  const fieldsForPage = (page) => myFields.filter(f => f.page === page);
  const unsignedFields = myFields.filter(f => !signatures[f.field_id]);
  const allSigned = hasPlacedFields ? unsignedFields.length === 0 : !!signatures['main'];

  // Canvas drawing functions
  const startDrawing = (x, y) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (x, y) => {
    if (!isDrawing || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing || !canvasRef.current) return;
    setIsDrawing(false);
    const data = canvasRef.current.toDataURL();
    if (activeField) {
      setSignatures(prev => ({ ...prev, [activeField]: data }));
    }
  };

  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    if (activeField) {
      setSignatures(prev => {
        const next = { ...prev };
        delete next[activeField];
        return next;
      });
    }
  };

  const handleComplete = () => {
    const sigList = Object.entries(signatures).map(([field_id, signature_data]) => {
      const f = myFields.find(mf => mf.field_id === field_id);
      const ft = f?.field_type || 'signature';
      const isExt = EXTERNAL_FIELD_TYPES.includes(ft) || f?.signer_type === 'external';
      const isSupFill = SUPERVISOR_FILL_TYPES.includes(ft) || f?.signer_type === 'supervisor_fill';
      return {
        field_id,
        signature_data, // for info fields this is the text value; for signature fields it's the base64 PNG
        field_type: ft,
        label: f?.label,
        is_info_field: f ? INFO_FIELD_TYPES.includes(ft) : false,
        is_external: isExt,
        is_supervisor_fill: isSupFill,
        external_name: externalMeta[field_id]?.name,
        external_company: externalMeta[field_id]?.company,
        external_role: externalMeta[field_id]?.role,
        signed_at: new Date().toISOString()
      };
    });
    onComplete(sigList);
  };

  const getFieldTypeColor = (field) => {
    // External kiosk fields
    if (field.field_type === 'signature_external') return { border: 'border-rose-500', bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-400' };
    if (field.field_type === 'external_name') return { border: 'border-pink-400', bg: 'bg-pink-50', text: 'text-pink-700', ring: 'ring-pink-400' };
    if (field.field_type === 'external_company') return { border: 'border-fuchsia-400', bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', ring: 'ring-fuchsia-400' };
    // Supervisor-fill
    if (field.field_type === 'text_supervisor_fill') return { border: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-400' };
    if (field.field_type === 'date_supervisor_fill') return { border: 'border-teal-500', bg: 'bg-teal-50', text: 'text-teal-700', ring: 'ring-teal-400' };
    // Info fields
    if (field.field_type === 'job_title') return { border: 'border-slate-400', bg: 'bg-slate-50', text: 'text-slate-700', ring: 'ring-slate-400' };
    if (field.field_type === 'ticket_number') return { border: 'border-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-400' };
    if (field.field_type === 'date_field') return { border: 'border-teal-400', bg: 'bg-teal-50', text: 'text-teal-700', ring: 'ring-teal-400' };
    if (field.field_type === 'notes') return { border: 'border-orange-400', bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-400' };
    if (field.field_type === 'printed_name') return { border: 'border-cyan-400', bg: 'bg-cyan-50', text: 'text-cyan-700', ring: 'ring-cyan-400' };
    if (field.field_type === 'company_name') return { border: 'border-indigo-400', bg: 'bg-indigo-50', text: 'text-indigo-700', ring: 'ring-indigo-400' };
    // Signature fields
    if (field.is_both || field.field_type === 'signature_both') return { border: 'border-purple-400', bg: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-400' };
    if (field.signer_type === 'supervisor' || field.field_type === 'signature_supervisor') return { border: 'border-blue-400', bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-400' };
    return { border: 'border-green-400', bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-400' };
  };

  const currentPageFields = fieldsForPage(currentPage);

  return (
    <div ref={containerRef} className="space-y-3" data-testid="pdf-document-signer">
      {/* Progress bar */}
      {hasPlacedFields && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="font-bold text-slate-600">Signatures Required</span>
              <span className="text-slate-500">{myFields.length - unsignedFields.length} / {myFields.length}</span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${myFields.length > 0 ? ((myFields.length - unsignedFields.length) / myFields.length) * 100 : 0}%` }}
              />
            </div>
          </div>
          {unsignedFields.length > 0 && (
            <button
              type="button"
              onClick={() => { setCurrentPage(unsignedFields[0].page); setActiveField(unsignedFields[0].field_id); }}
              className="text-xs px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg font-bold hover:bg-orange-200"
            >
              Next unsigned
            </button>
          )}
        </div>
      )}

      {/* PDF Viewer */}
      <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-slate-200">
        <div className="flex items-center justify-between p-2 bg-slate-700 text-white">
          <button
            type="button"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="p-1.5 rounded hover:bg-slate-600 disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium">Page {currentPage} of {numPages || '...'}</span>
          <button
            type="button"
            onClick={() => setCurrentPage(p => Math.min(numPages || 1, p + 1))}
            disabled={currentPage >= (numPages || 1)}
            className="p-1.5 rounded hover:bg-slate-600 disabled:opacity-30"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="relative mx-auto" style={{ width: pageWidth }}>
          <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<div className="p-12 text-center text-slate-500">Loading PDF...</div>}>
            <Page pageNumber={currentPage} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>

          {/* Signature field overlays on PDF */}
          {currentPageFields.map(field => {
            const colors = getFieldTypeColor(field);
            const isSigned = !!signatures[field.field_id];
            const isActive = activeField === field.field_id;
            return (
              <div
                key={field.field_id}
                className={`absolute border-2 rounded transition-all cursor-pointer ${
                  isSigned 
                    ? 'border-green-500 bg-green-50/80' 
                    : isActive 
                      ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}` 
                      : `${colors.border} ${colors.bg}/60 border-dashed hover:${colors.bg}`
                }`}
                style={{
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  width: `${field.width}%`,
                  height: `${field.height}%`,
                  zIndex: isActive ? 20 : 10
                }}
                onClick={() => !isSigned && setActiveField(field.field_id)}
                data-testid={`sign-field-${field.field_id}`}
              >
                <div className="flex items-center justify-center h-full px-1 overflow-hidden">
                  {isSigned ? (
                    isInfoField(field) ? (
                      <span className={`text-[11px] font-medium text-slate-700 truncate px-1`}>
                        {signatures[field.field_id]}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Check className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-[10px] font-bold text-green-600 uppercase">Signed</span>
                      </div>
                    )
                  ) : (
                    <span className={`text-[10px] font-bold uppercase ${colors.text} truncate`}>
                      {isActive ? (isInfoField(field) ? 'Fill below' : 'Sign below') : field.label}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active field input: text/date for info fields, canvas for signatures */}
      {(() => {
        const active = activeField ? myFields.find(f => f.field_id === activeField) : null;
        const showInfoInput = active && isInfoField(active);
        const showCanvas = (activeField && !showInfoInput) || !hasPlacedFields;
        if (!activeField && hasPlacedFields) return null;

        if (showInfoInput) {
          return (
            <div className="border-2 border-orange-300 rounded-lg p-3 bg-orange-50" data-testid="info-field-input-area">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold uppercase text-slate-600">
                  {active.label}
                </label>
                <button
                  type="button"
                  onClick={() => setSignatures(prev => { const n = { ...prev }; delete n[active.field_id]; return n; })}
                  className="text-xs px-2 py-1 bg-white rounded border hover:bg-slate-50"
                >
                  Clear
                </button>
              </div>
              {active.field_type === 'date_field' || active.field_type === 'date_supervisor_fill' ? (
                <input
                  type="date"
                  value={signatures[active.field_id] || ''}
                  onChange={(e) => setSignatures(prev => ({ ...prev, [active.field_id]: e.target.value }))}
                  className="w-full border-2 border-slate-200 rounded bg-white px-3 py-3 text-base"
                  data-testid={`info-input-${active.field_id}`}
                />
              ) : active.field_type === 'notes' || active.field_type === 'text_supervisor_fill' ? (
                <textarea
                  value={signatures[active.field_id] || ''}
                  onChange={(e) => setSignatures(prev => ({ ...prev, [active.field_id]: e.target.value }))}
                  placeholder={active.field_type === 'text_supervisor_fill' ? "Fill in (e.g. today's work, hazards, equipment used)..." : "Enter notes or comments..."}
                  rows={3}
                  className="w-full border-2 border-slate-200 rounded bg-white px-3 py-2 text-base resize-none"
                  data-testid={`info-input-${active.field_id}`}
                />
              ) : (
                <input
                  type="text"
                  value={signatures[active.field_id] || ''}
                  onChange={(e) => setSignatures(prev => ({ ...prev, [active.field_id]: e.target.value }))}
                  placeholder={
                    active.field_type === 'job_title' ? 'e.g. Dogman, Operator, Supervisor' :
                    active.field_type === 'ticket_number' ? 'e.g. HRWL 12345678' :
                    active.field_type === 'printed_name' ? 'Full name' :
                    active.field_type === 'company_name' ? 'Company name' :
                    active.field_type === 'external_name' ? 'External person name' :
                    active.field_type === 'external_company' ? 'External company / subbie' :
                    ''
                  }
                  className="w-full border-2 border-slate-200 rounded bg-white px-3 py-3 text-base"
                  data-testid={`info-input-${active.field_id}`}
                />
              )}
              <button
                type="button"
                onClick={() => setActiveField(null)}
                disabled={!signatures[active.field_id]}
                className={`w-full mt-2 py-2 rounded-lg font-bold text-sm ${signatures[active.field_id] ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-200 text-slate-400'}`}
              >
                Done
              </button>
            </div>
          );
        }

        if (showCanvas) {
          const activeFld = activeField ? myFields.find(f => f.field_id === activeField) : null;
          const isExternalSig = activeFld && (activeFld.field_type === 'signature_external' || activeFld.signer_type === 'external');
          return (
            <div className={`border-2 rounded-lg p-3 ${isExternalSig ? 'border-rose-400 bg-rose-50' : 'border-orange-300 bg-orange-50'}`} data-testid="signature-canvas-area">
              {isExternalSig && (
                <div className="mb-3 p-3 bg-white rounded border border-rose-200" data-testid="external-kiosk-banner">
                  <p className="text-xs font-bold uppercase text-rose-700 mb-1">Kiosk mode — external signer</p>
                  <p className="text-xs text-slate-600 mb-2">Capture the subbie/visitor's name and company, then hand them the tablet to sign.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Name of person signing"
                      value={externalMeta[activeField]?.name || ''}
                      onChange={(e) => setExternalMeta(m => ({ ...m, [activeField]: { ...(m[activeField] || {}), name: e.target.value } }))}
                      className="border-2 border-slate-200 rounded px-2 py-2 text-sm"
                      data-testid="external-signer-name"
                    />
                    <input
                      type="text"
                      placeholder="Company / subbie (optional)"
                      value={externalMeta[activeField]?.company || ''}
                      onChange={(e) => setExternalMeta(m => ({ ...m, [activeField]: { ...(m[activeField] || {}), company: e.target.value } }))}
                      className="border-2 border-slate-200 rounded px-2 py-2 text-sm"
                      data-testid="external-signer-company"
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold uppercase text-slate-600">
                  {activeField 
                    ? `Sign: ${(myFields.find(f => f.field_id === activeField)?.label || 'Signature')}`
                    : 'Your Signature *'}
                </label>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-xs px-2 py-1 bg-white rounded border hover:bg-slate-50"
                >
                  Clear
                </button>
              </div>
              <canvas
                ref={canvasRef}
                width={500}
                height={120}
                className="w-full border-2 border-slate-200 rounded bg-white cursor-crosshair"
                style={{ touchAction: 'none' }}
                onMouseDown={(e) => { const c = getCanvasCoords(e, canvasRef.current); startDrawing(c.x, c.y); }}
                onMouseMove={(e) => { const c = getCanvasCoords(e, canvasRef.current); draw(c.x, c.y); }}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={(e) => { e.preventDefault(); const c = getCanvasCoords(e, canvasRef.current); startDrawing(c.x, c.y); }}
                onTouchMove={(e) => { e.preventDefault(); const c = getCanvasCoords(e, canvasRef.current); draw(c.x, c.y); }}
                onTouchEnd={stopDrawing}
              />
              {!hasPlacedFields && !signatures['main'] && (
                <p className="text-xs text-slate-500 mt-1">Draw your signature above</p>
              )}
            </div>
          );
        }
        return null;
      })()}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <button
          type="button"
          onClick={handleComplete}
          disabled={!allSigned}
          className={`flex-1 py-3 px-6 rounded-xl font-bold text-base transition-all touch-manipulation ${
            allSigned 
              ? 'bg-green-600 text-white hover:bg-green-700' 
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
          style={{ minHeight: '48px' }}
          data-testid="submit-signatures-btn"
        >
          <div className="flex items-center justify-center gap-2">
            <Check className="w-5 h-5" />
            {allSigned ? 'Submit Signatures' : `Sign all fields (${unsignedFields.length} remaining)`}
          </div>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="py-3 px-6 rounded-xl font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 touch-manipulation"
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default PDFDocumentSigner;
